import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Sparkles, ShieldCheck, CreditCard } from "lucide-react";
import { useRegion } from "../context/RegionContext";
import RegionSelector from "../components/RegionSelector";

export default function Pricing() {
    const [planType, setPlanType] = useState("monthly");
    const { region } = useRegion();

    const basicRate = region.pricing.basic[planType];
    const proRate = region.pricing.pro[planType];

    return (
        <div className="min-h-screen p-6 sm:p-12 bg-base transition-colors duration-300">
            <SEO
                title="Simple Global Pricing | blinqbooks"
                description={`Transparent multi-currency pricing plans for businesses in ${region.name} and worldwide. From solo founders to growing teams.`}
            />
            
            <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold uppercase tracking-wider mb-4 border border-accent/20">
                    <span className="text-sm">{region.flag}</span> Region: {region.name} ({region.currency})
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 text-primary tracking-tight">
                    Simple, Transparent Pricing
                </h1>
                <p className="text-secondary text-base sm:text-lg mb-8">
                    Choose the ideal plan for your business. Billed in {region.currency} with full {region.taxName} compliance.
                </p>

                {/* Region & Currency Switcher Callout */}
                <div className="inline-flex items-center gap-3 p-2 px-4 rounded-2xl bg-surface border border-subtle shadow-sm mb-12">
                    <span className="text-xs text-secondary font-medium">Viewing prices for:</span>
                    <RegionSelector />
                </div>
            </div>

            {/* Plan Toggle */}
            <div className="flex justify-center mb-16">
                <div className="bg-slate-500/[0.06] dark:bg-white/[0.04] p-1.5 rounded-2xl inline-flex relative border border-slate-200/60 dark:border-slate-800">
                    <button
                        onClick={() => setPlanType("monthly")}
                        className={`px-7 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${planType === "monthly"
                            ? "bg-accent text-white shadow-md shadow-accent/20"
                            : "text-secondary hover:text-primary"
                            }`}
                    >
                        Monthly Billing
                    </button>
                    <button
                        onClick={() => setPlanType("annual")}
                        className={`px-7 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center gap-1.5 ${planType === "annual"
                            ? "bg-accent text-white shadow-md shadow-accent/20"
                            : "text-secondary hover:text-primary"
                            }`}
                    >
                        Annual Billing
                        <span className="text-[10px] bg-emerald-500 text-white font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                            Save 20%
                        </span>
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                {/* Basic Plan Card */}
                <div className="card card-hover flex flex-col bg-surface dark:bg-slate-900/90 border border-subtle dark:border-slate-800 p-8 rounded-[2rem] shadow-sm hover:shadow-md transition-all">
                    <h2 className="text-xl font-bold mb-3 text-primary dark:text-white">Basic Plan</h2>
                    <p className="text-4xl font-black mb-2 text-primary dark:text-white flex items-baseline">
                        <span>{region.symbol}{basicRate}</span>
                        <span className="text-base font-normal text-secondary dark:text-slate-400 ml-1">/mo</span>
                    </p>
                    <p className="text-xs text-secondary dark:text-slate-400 mb-4">
                        {planType === "annual" ? "Billed annually" : "Billed monthly, cancel anytime"}
                    </p>
                    <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mb-6 bg-emerald-50 dark:bg-emerald-950/60 py-1.5 px-3 rounded-full w-fit border border-emerald-500/20 dark:border-emerald-500/30">
                        14-Day Free Trial Included
                    </div>

                    <ul className="space-y-3.5 mb-8 flex-1 text-sm">
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-emerald-500 font-bold mr-2.5">✓</span> Single user account
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-emerald-500 font-bold mr-2.5">✓</span> Multi-currency invoicing
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-emerald-500 font-bold mr-2.5">✓</span> {region.taxName} calculations
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-emerald-500 font-bold mr-2.5">✓</span> WhatsApp, Email & SMS delivery
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-emerald-500 font-bold mr-2.5">✓</span> Online payment links
                        </li>
                    </ul>

                    <Link to="/signup" className="btn-secondary w-full text-center flex items-center justify-center py-3.5 px-4 text-sm sm:text-base font-bold whitespace-nowrap mt-auto">
                        Start Free Trial
                    </Link>
                </div>

                {/* Pro Plan Card (Highlight) */}
                <div className="card relative flex flex-col transform md:-translate-y-4 md:scale-105 bg-surface dark:bg-slate-900 border-2 border-accent p-8 rounded-[2rem] shadow-xl shadow-accent/10 z-10">
                    <div className="absolute top-0 right-0 bg-accent text-white text-[11px] font-extrabold px-4 py-1.5 rounded-tr-[1.8rem] rounded-bl-xl flex items-center gap-1 uppercase tracking-wider shadow-sm">
                        <Sparkles className="w-3.5 h-3.5" />
                        Most Popular
                    </div>
                    <h2 className="text-xl font-bold mb-3 text-primary dark:text-white">Pro Business</h2>
                    <p className="text-4xl font-black mb-2 text-primary dark:text-white flex items-baseline">
                        <span>{region.symbol}{proRate}</span>
                        <span className="text-base font-normal text-secondary dark:text-slate-400 ml-1">/mo</span>
                    </p>
                    <p className="text-xs text-secondary dark:text-slate-400 mb-4">
                        {planType === "annual" ? "Billed annually (Save 20%)" : "Billed monthly, cancel anytime"}
                    </p>
                    <div className="text-xs font-bold text-blue-600 dark:text-blue-300 mb-6 bg-blue-50 dark:bg-blue-950/60 py-1.5 px-3 rounded-full w-fit border border-blue-500/20 dark:border-blue-500/30">
                        {region.complianceBadge}
                    </div>

                    <ul className="space-y-3.5 mb-8 flex-1 text-sm">
                        <li className="flex items-center text-secondary dark:text-slate-200 font-medium">
                            <span className="text-accent font-bold mr-2.5">✓</span> <strong className="text-primary dark:text-white">All Basic features included</strong>
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-accent font-bold mr-2.5">✓</span> Multi-user team access (up to 5)
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-accent font-bold mr-2.5">✓</span> <strong className="text-primary dark:text-white">AI receipt & expense OCR</strong>
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-accent font-bold mr-2.5">✓</span> Automated payment follow-up nudges
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-accent font-bold mr-2.5">✓</span> Automated bank feeds & reconciliation
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-accent font-bold mr-2.5">✓</span> Priority 24/7 global support
                        </li>
                    </ul>

                    <Link to="/signup" className="btn-primary w-full text-center flex items-center justify-center py-3.5 px-4 text-sm sm:text-base font-bold whitespace-nowrap shadow-lg shadow-accent/25 mt-auto">
                        Get Started
                    </Link>
                </div>

                {/* Enterprise Card */}
                <div className="card card-hover flex flex-col bg-surface dark:bg-slate-900/90 border border-subtle dark:border-slate-800 p-8 rounded-[2rem] shadow-sm hover:shadow-md transition-all">
                    <h2 className="text-xl font-bold mb-3 text-primary dark:text-white">Enterprise</h2>
                    <p className="text-4xl font-black mb-2 text-primary dark:text-white">
                        Custom
                    </p>
                    <p className="text-xs text-secondary dark:text-slate-400 mb-4">
                        For multi-entity & global organizations
                    </p>
                    <div className="text-xs font-bold text-purple-600 dark:text-purple-300 mb-6 bg-purple-50 dark:bg-purple-950/60 py-1.5 px-3 rounded-full w-fit border border-purple-500/20 dark:border-purple-500/30">
                        Dedicated Migration & SLA
                    </div>

                    <ul className="space-y-3.5 mb-8 flex-1 text-sm">
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-purple-500 font-bold mr-2.5">✓</span> Unlimited users & branches
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-purple-500 font-bold mr-2.5">✓</span> Multi-currency consolidation
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-purple-500 font-bold mr-2.5">✓</span> Custom ERP & CRM integrations
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-purple-500 font-bold mr-2.5">✓</span> Dedicated account manager
                        </li>
                        <li className="flex items-center text-secondary dark:text-slate-200">
                            <span className="text-purple-500 font-bold mr-2.5">✓</span> 99.9% uptime SLA & SOC 2 audit
                        </li>
                    </ul>

                    <Link
                        to="/contact"
                        className="btn-secondary w-full text-center flex items-center justify-center py-3.5 px-4 text-sm sm:text-base font-bold whitespace-nowrap mt-auto"
                    >
                        Contact Enterprise Sales
                    </Link>
                </div>
            </div>

            {/* Global Trust & Payment Methods Banner */}
            <div className="max-w-5xl mx-auto rounded-3xl p-6 sm:p-7 bg-surface dark:bg-slate-900/90 border border-subtle dark:border-slate-800 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                    <div className="text-left space-y-1">
                        <div className="flex items-center gap-2 font-bold text-sm text-primary dark:text-white">
                            <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                            <span>Global Compliance in {region.name}</span>
                        </div>
                        <p className="text-xs text-secondary dark:text-slate-300">
                            Supports {region.taxName}, IFRS & GAAP bookkeeping standards. Localized invoice formatting.
                        </p>
                    </div>

                    <div className="flex items-center flex-wrap gap-2.5 text-xs md:justify-end shrink-0">
                        <span className="text-secondary dark:text-slate-300 font-semibold flex items-center gap-1.5 shrink-0">
                            <CreditCard className="w-3.5 h-3.5 text-slate-400" /> Accepted:
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {region.paymentBadges.map((badge, idx) => (
                                <span
                                    key={idx}
                                    className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold text-[11px] border border-slate-200 dark:border-slate-700 whitespace-nowrap"
                                >
                                    {badge}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}